import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import CustomerLayout from '../components/CustomerLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Upload, Video, Play, Edit, Trash2, Eye, GripVertical, AlertCircle, ArrowLeft, List, Image as ImageIcon, Maximize, Images } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Poster Card Component (for posters inside playlist)
const SortablePosterCard = ({ poster, imageBlobUrls, onPreview, onEdit, onDelete, onToggleHidden }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: poster.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : poster.is_hidden ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        background: poster.is_hidden ? 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)' : 'linear-gradient(135deg, #1a1f2e 0%, #2a3142 100%)',
        borderRadius: '12px',
        overflow: 'hidden',
        border: poster.is_hidden ? '1px solid rgba(255, 100, 100, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative'
      }}>
        {/* Drag handle */}
        <div 
          {...attributes}
          {...listeners}
          style={{
            position: 'absolute',
            top: '0.5rem',
            left: '0.5rem',
            background: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '6px',
            padding: '0.5rem',
            cursor: 'grab',
            zIndex: 10
          }}
        >
          <GripVertical size={20} color="white" />
        </div>

        {/* Thumbnail */}
        <div style={{
          height: '140px',
          background: '#1a1f2e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          {poster.poster_type === 'image' && poster.image_url && (
            <img
              src={imageBlobUrls[poster.id] || (poster.image_url.startsWith('http') ? poster.image_url : `${BACKEND_URL}${poster.image_url}`)}
              alt={poster.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          )}
          {poster.poster_type === 'video' && poster.video_thumbnail_url && (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <img
                src={poster.video_thumbnail_url}
                alt={poster.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0, 0, 0, 0.6)',
                borderRadius: '50%',
                padding: '1rem'
              }}>
                <Play size={30} color="white" />
              </div>
            </div>
          )}
          {poster.poster_type === 'iframe' && (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            }}>
              <Maximize size={50} color="white" />
            </div>
          )}
          {poster.poster_type === 'gallery' && (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
            }}>
              <Images size={50} color="white" />
              <span style={{ color: 'white', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                {poster.gallery_images?.length || 0} bilder
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '1rem' }}>
          <h3 style={{ 
            color: poster.is_hidden ? '#999' : 'white', 
            marginBottom: '0.5rem', 
            fontSize: '1rem',
            textDecoration: poster.is_hidden ? 'line-through' : 'none'
          }}>
            {poster.title} {poster.is_hidden && '(Skjult)'}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <span style={{ 
              background: 'rgba(59, 130, 246, 0.2)', 
              color: '#60a5fa', 
              padding: '0.25rem 0.75rem', 
              borderRadius: '999px', 
              fontSize: '0.75rem',
              fontWeight: '500'
            }}>
              {poster.duration_seconds}s
            </span>
            {poster.poster_type === 'video' && poster.audio_enabled && (
              <span style={{ 
                background: 'rgba(34, 197, 94, 0.2)', 
                color: '#4ade80', 
                padding: '0.25rem 0.75rem', 
                borderRadius: '999px', 
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                🔊 Lyd
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => onPreview(poster)}
                style={{
                  padding: '0.5rem',
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
                title="Forhåndsvisning"
              >
                <Eye size={16} />
              </button>
              <button
                onClick={() => onEdit(poster)}
                style={{
                  padding: '0.5rem',
                  background: 'rgba(234, 179, 8, 0.2)',
                  color: '#fbbf24',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
                title="Rediger"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => onToggleHidden(poster)}
                style={{
                  padding: '0.5rem',
                  background: poster.is_hidden ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                  color: poster.is_hidden ? '#4ade80' : '#f87171',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}
                title={poster.is_hidden ? "Vis plakat" : "Skjul plakat"}
              >
                {poster.is_hidden ? 'Vis' : 'Skjul'}
              </button>
            </div>
            <button
              onClick={() => onDelete(poster.id)}
              style={{
                padding: '0.5rem',
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#f87171',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
              title="Slett"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomerPosters = () => {
  const { t } = useTranslation();
  // State for playlists
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  
  // State for posters (in selected playlist)
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [showPosterModal, setShowPosterModal] = useState(false);
  const [editingPoster, setEditingPoster] = useState(null);
  const [posterModalType, setPosterModalType] = useState('image'); // 'image' or 'video'
  
  // Form states for playlist
  const [playlistTitle, setPlaylistTitle] = useState('');
  const [playlistHideDuration, setPlaylistHideDuration] = useState(300);
  const [playlistScreens, setPlaylistScreens] = useState([]);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDays, setScheduleDays] = useState([]);
  const [scheduleTimeFrom, setScheduleTimeFrom] = useState('00:00');
  const [scheduleTimeTo, setScheduleTimeTo] = useState('23:59');
  
  // Form states for poster
  const [posterTitle, setPosterTitle] = useState('');
  const [posterDuration, setPosterDuration] = useState(10);
  const [videoUrl, setVideoUrl] = useState('');
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [videoTotalDuration, setVideoTotalDuration] = useState(0);
  const [videoStartTime, setVideoStartTime] = useState(0);
  const [videoEndTime, setVideoEndTime] = useState(0);
  const [fetchingVideoDuration, setFetchingVideoDuration] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeReloadInterval, setIframeReloadInterval] = useState(5);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryImageDuration, setGalleryImageDuration] = useState(5);
  const [addingGalleryImages, setAddingGalleryImages] = useState(false);
  const [removingGalleryImage, setRemovingGalleryImage] = useState(null);
  
  // Other states
  const [previewPoster, setPreviewPoster] = useState(null);
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [screens, setScreens] = useState([]);
  const [organization, setOrganization] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchPlaylists();
    fetchScreens();
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/organizations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganization(response.data);
    } catch (error) {
      console.error('Error fetching organization:', error);
    }
  };

  useEffect(() => {
    if (selectedPlaylist) {
      fetchPosters(selectedPlaylist.id);
    }
  }, [selectedPlaylist]);

  const fetchPlaylists = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/playlists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaylists(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching playlists:', error);
      toast.error(t('customerPosters.errorLoadingPlaylists'));
      setLoading(false);
    }
  };

  const fetchPosters = async (playlistId) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/playlists/${playlistId}/posters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosters(response.data);
      
      // For Cloudinary images, use URL directly. For local images, fetch as blob.
      const newBlobUrls = {};
      for (const poster of response.data) {
        if (poster.poster_type === 'image' && poster.image_url) {
          // If it's a Cloudinary URL (starts with http), use it directly
          if (poster.image_url.startsWith('http://') || poster.image_url.startsWith('https://')) {
            newBlobUrls[poster.id] = poster.image_url;
          } else {
            // For local files, fetch as blob (requires authentication)
            try {
              const imageUrl = `${BACKEND_URL}${poster.image_url}`;
              const imgResponse = await axios.get(imageUrl, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
              });
              newBlobUrls[poster.id] = URL.createObjectURL(imgResponse.data);
            } catch (err) {
              console.error('Error loading image:', err);
            }
          }
        }
      }
      setImageBlobUrls(newBlobUrls);
    } catch (error) {
      console.error('Error fetching posters:', error);
      toast.error(t('customerPosters.errorLoadingPosters'));
    }
  };

  const fetchScreens = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScreens(response.data);
    } catch (error) {
      console.error('Error fetching screens:', error);
    }
  };

  // Playlist CRUD operations
  const handleCreatePlaylist = () => {
    setEditingPlaylist(null);
    setPlaylistTitle('');
    setPlaylistHideDuration(300);
    setPlaylistScreens([]);
    setScheduleEnabled(false);
    setScheduleDays([]);
    setScheduleTimeFrom('00:00');
    setScheduleTimeTo('23:59');
    setShowPlaylistModal(true);
  };

  const handleEditPlaylist = (playlist) => {
    setEditingPlaylist(playlist);
    setPlaylistTitle(playlist.title);
    setPlaylistHideDuration(playlist.hide_duration);
    setPlaylistScreens(playlist.screen_ids || []);
    setScheduleEnabled(playlist.schedule_enabled || false);
    setScheduleDays(playlist.schedule_days || []);
    setScheduleTimeFrom(playlist.schedule_time_from || '00:00');
    setScheduleTimeTo(playlist.schedule_time_to || '23:59');
    setShowPlaylistModal(true);
  };

  const handleSavePlaylist = async (e) => {
    e.preventDefault();
    
    try {
      const token = getToken();
      const payload = {
        title: playlistTitle,
        hide_duration: playlistHideDuration,
        screen_ids: playlistScreens,
        schedule_enabled: scheduleEnabled,
        schedule_days: scheduleDays,
        schedule_time_from: scheduleEnabled ? scheduleTimeFrom : null,
        schedule_time_to: scheduleEnabled ? scheduleTimeTo : null,
        organization_id: '' // Backend will set this
      };

      if (editingPlaylist) {
        await axios.put(`${API}/playlists/${editingPlaylist.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerPosters.playlistUpdated'));
      } else {
        await axios.post(`${API}/playlists`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerPosters.playlistCreated'));
      }

      setShowPlaylistModal(false);
      fetchPlaylists();
    } catch (error) {
      console.error('Error saving playlist:', error);
      toast.error(t('customerPosters.errorSavingPlaylist'));
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!await confirm.danger(t('customerPosters.confirmDeletePlaylist'))) {
      return;
    }

    try {
      const token = getToken();
      await axios.delete(`${API}/playlists/${playlistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerPosters.playlistDeleted'));
      fetchPlaylists();
      if (selectedPlaylist && selectedPlaylist.id === playlistId) {
        setSelectedPlaylist(null);
        setPosters([]);
      }
    } catch (error) {
      console.error('Error deleting playlist:', error);
      toast.error(t('customerPosters.errorDeletingPlaylist'));
    }
  };

  // Poster CRUD operations
  const handleUploadImage = () => {
    if (!selectedPlaylist) {
      toast.error(t('customerPosters.selectPlaylistFirst'));
      return;
    }
    setEditingPoster(null);
    setPosterTitle('');
    setPosterDuration(10);
    setSelectedFile(null);
    setFilePreview(null);
    setPosterModalType('image');
    setShowPosterModal(true);
  };

  const handleUploadVideo = () => {
    if (!selectedPlaylist) {
      toast.error(t('customerPosters.selectPlaylistFirst'));
      return;
    }
    setEditingPoster(null);
    setPosterTitle('');
    setPosterDuration(10);
    setVideoUrl('');
    setAudioEnabled(false);
    setPosterModalType('video');
    setShowPosterModal(true);
  };

  const handleUploadIframe = () => {
    if (!selectedPlaylist) {
      toast.error(t('customerPosters.selectPlaylistFirst'));
      return;
    }
    setEditingPoster(null);
    setPosterTitle('');
    setPosterDuration(30);
    setIframeUrl('');
    setIframeReloadInterval(5);
    setPosterModalType('iframe');
    setShowPosterModal(true);
  };

  const handleUploadGallery = () => {
    if (!selectedPlaylist) {
      toast.error(t('customerPosters.selectPlaylistFirst'));
      return;
    }
    setEditingPoster(null);
    setPosterTitle('');
    setGalleryFiles([]);
    setGalleryImageDuration(5);
    setPosterModalType('gallery');
    setShowPosterModal(true);
  };

  const handleEditPoster = (poster) => {
    setPreviewPoster(null);
    setEditingPoster(poster);
    setPosterTitle(poster.title);
    setPosterDuration(poster.duration_seconds);
    setAudioEnabled(poster.audio_enabled || false);
    setPosterModalType(poster.poster_type);
    
    // Set video-specific fields if editing a video poster
    if (poster.poster_type === 'video') {
      setVideoUrl(poster.video_url || '');
      setVideoTotalDuration(poster.video_duration || 0);
      setVideoStartTime(poster.video_start_time || 0);
      setVideoEndTime(poster.video_end_time || poster.video_duration || 0);
    }
    
    // Set iframe-specific fields if editing an iframe poster
    if (poster.poster_type === 'iframe') {
      setIframeUrl(poster.iframe_url || '');
      setIframeReloadInterval(poster.iframe_reload_interval || 5);
    }
    
    // Set gallery-specific fields if editing a gallery poster
    if (poster.poster_type === 'gallery') {
      setGalleryImageDuration(poster.gallery_image_duration || 5);
      // Show existing images when editing
      setGalleryFiles([]);  // Clear file list since we can't re-upload same files
    }
    
    setShowPosterModal(true);
  };

  // Add more images to an existing gallery poster
  const handleAddGalleryImages = async (files) => {
    if (!editingPoster || editingPoster.poster_type !== 'gallery') return;
    
    if (files.length === 0) return;
    
    const currentCount = editingPoster.gallery_images?.length || 0;
    if (currentCount + files.length > 50) {
      toast.error(`Maksimum 50 bilder tillatt. Du har ${currentCount} bilder, kan legge til ${50 - currentCount} til.`);
      return;
    }
    
    setAddingGalleryImages(true);
    
    try {
      const token = getToken();
      const formData = new FormData();
      
      files.forEach((file) => {
        formData.append('images', file);
      });
      
      const response = await axios.post(
        `${API}/posters/${editingPoster.id}/gallery/add-images`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Update editingPoster with new images
      setEditingPoster(response.data);
      
      // Update posters list
      setPosters(posters.map(p => p.id === editingPoster.id ? response.data : p));
      
      toast.success(`${files.length} bilde${files.length !== 1 ? 'r' : ''} lagt til galleriet`);
      setGalleryFiles([]);
    } catch (error) {
      console.error('Error adding gallery images:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke legge til bilder');
    } finally {
      setAddingGalleryImages(false);
    }
  };

  // Remove a single image from gallery poster
  const handleRemoveGalleryImage = async (imageIndex) => {
    if (!editingPoster || editingPoster.poster_type !== 'gallery') return;
    
    const imageCount = editingPoster.gallery_images?.length || 0;
    if (imageCount <= 1) {
      toast.error('Kan ikke fjerne siste bilde. Slett plakaten i stedet.');
      return;
    }
    
    const confirmed = await confirm.danger(
      'Er du sikker på at du vil fjerne dette bildet fra galleriet?'
    );
    
    if (!confirmed) return;
    
    setRemovingGalleryImage(imageIndex);
    
    try {
      const token = getToken();
      
      const response = await axios.delete(
        `${API}/posters/${editingPoster.id}/gallery/image/${imageIndex}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update editingPoster with updated images
      setEditingPoster(response.data);
      
      // Update posters list
      setPosters(posters.map(p => p.id === editingPoster.id ? response.data : p));
      
      toast.success('Bilde fjernet fra galleriet');
    } catch (error) {
      console.error('Error removing gallery image:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke fjerne bilde');
    } finally {
      setRemovingGalleryImage(null);
    }
  };

  const handleSavePoster = async (e) => {
    e.preventDefault();

    if (!selectedPlaylist) {
      toast.error('Ingen spilleliste valgt');
      return;
    }

    try {
      const token = getToken();

      if (editingPoster) {
        // Update existing poster
        const updateData = {
          title: posterTitle,
          duration_seconds: posterDuration,
          audio_enabled: audioEnabled
        };
        
        // Add video-specific fields if editing a video poster
        if (editingPoster.poster_type === 'video' && videoTotalDuration > 0) {
          updateData.video_start_time = videoStartTime;
          updateData.video_end_time = videoEndTime;
        }
        
        // Add iframe-specific fields if editing an iframe poster
        if (editingPoster.poster_type === 'iframe') {
          updateData.iframe_url = iframeUrl;
          updateData.iframe_reload_interval = iframeReloadInterval;
        }
        
        // Add gallery-specific fields if editing a gallery poster
        if (editingPoster.poster_type === 'gallery') {
          updateData.gallery_image_duration = galleryImageDuration;
          // Update total duration based on new image duration
          const imageCount = editingPoster.gallery_images?.length || 0;
          updateData.duration_seconds = imageCount * galleryImageDuration;
        }
        
        await axios.put(`${API}/posters/${editingPoster.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerPosters.posterUpdated'));
      } else {
        // Create new poster
        if (posterModalType === 'image') {
          if (!selectedFile) {
            toast.error(t('customerPosters.selectImageFirst'));
            return;
          }

          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('title', posterTitle);
          formData.append('playlist_id', selectedPlaylist.id);
          formData.append('duration_seconds', posterDuration);

          await axios.post(`${API}/posters/upload-image`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success(t('customerPosters.imageUploaded'));
        } else if (posterModalType === 'video') {
          if (!videoUrl) {
            toast.error(t('customerPosters.enterVideoUrl'));
            return;
          }

          const formData = new FormData();
          formData.append('video_url', videoUrl);
          formData.append('title', posterTitle);
          formData.append('playlist_id', selectedPlaylist.id);
          formData.append('duration_seconds', posterDuration);
          formData.append('audio_enabled', audioEnabled);
          
          // Add start and end time if video duration was fetched
          if (videoTotalDuration > 0) {
            formData.append('video_start_time', videoStartTime);
            formData.append('video_end_time', videoEndTime);
          }

          await axios.post(`${API}/posters/create-video`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success(t('customerPosters.videoAdded'));
        } else if (posterModalType === 'iframe') {
          if (!iframeUrl) {
            toast.error(t('customerPosters.enterIframeUrl'));
            return;
          }

          const formData = new FormData();
          formData.append('iframe_url', iframeUrl);
          formData.append('title', posterTitle);
          formData.append('playlist_id', selectedPlaylist.id);
          formData.append('duration_seconds', posterDuration);
          formData.append('iframe_reload_interval', iframeReloadInterval);

          await axios.post(`${API}/posters/create-iframe`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success(t('customerPosters.iframePosterAdded'));
        } else if (posterModalType === 'gallery') {
          if (galleryFiles.length === 0) {
            toast.error(t('customerPosters.selectAtLeastOneImage'));
            return;
          }

          const formData = new FormData();
          formData.append('title', posterTitle);
          formData.append('playlist_id', selectedPlaylist.id);
          formData.append('gallery_image_duration', galleryImageDuration);
          
          // Append all image files
          galleryFiles.forEach((file) => {
            formData.append('images', file);
          });

          await axios.post(`${API}/posters/create-gallery`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success(t('customerPosters.galleryAdded', { count: galleryFiles.length }));
        }
      }

      setShowPosterModal(false);
      resetPosterForm();
      fetchPosters(selectedPlaylist.id);
      fetchPlaylists(); // Update playlist totals
    } catch (error) {
      console.error('Error saving poster:', error);
      toast.error(error.response?.data?.detail || t('customerPosters.errorSavingPoster'));
    }
  };

  const handleDeletePoster = async (posterId) => {
    if (!await confirm.danger(t('customerPosters.confirmDeletePoster'))) {
      return;
    }

    try {
      const token = getToken();
      await axios.delete(`${API}/posters/${posterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerPosters.posterDeleted'));
      fetchPosters(selectedPlaylist.id);
      fetchPlaylists(); // Update playlist totals
    } catch (error) {
      console.error('Error deleting poster:', error);
      const errorMsg = error.response?.data?.detail || t('customerPosters.errorDeletingPoster');
      toast.error(errorMsg);
    }
  };

  const handleTogglePlaylistActive = async (playlist, e) => {
    e.stopPropagation();
    
    try {
      const token = getToken();
      await axios.put(`${API}/playlists/${playlist.id}`, {
        is_active: !playlist.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(playlist.is_active ? t('customerPosters.playlistDeactivated') : t('customerPosters.playlistActivated'));
      fetchPlaylists();
    } catch (error) {
      console.error('Error toggling playlist:', error);
      toast.error(t('customerPosters.errorChangingStatus'));
    }
  };

  const handleToggleHidden = async (poster) => {
    try {
      const token = getToken();
      await axios.put(`${API}/posters/${poster.id}`, {
        is_hidden: !poster.is_hidden
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(poster.is_hidden ? t('customerPosters.posterShown') : t('customerPosters.posterHidden'));
      fetchPosters(selectedPlaylist.id);
      fetchPlaylists(); // Update playlist totals
    } catch (error) {
      console.error('Error toggling poster visibility:', error);
      toast.error(t('customerPosters.errorChangingVisibility'));
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    if (!selectedPlaylist) {
      toast.error(t('customerPosters.noPlaylistSelected'));
      return;
    }

    const oldIndex = posters.findIndex(p => p.id === active.id);
    const newIndex = posters.findIndex(p => p.id === over.id);
    
    const newPosters = arrayMove(posters, oldIndex, newIndex);
    setPosters(newPosters);

    // Update backend
    try {
      const token = getToken();
      const posterIds = newPosters.map(p => p.id);
      
      await axios.put(`${API}/posters/reorder`, {
        poster_ids: posterIds,
        playlist_id: selectedPlaylist.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('customerPosters.orderUpdated'));
    } catch (error) {
      console.error('Error reordering posters:', error);
      toast.error(t('customerPosters.errorUpdatingOrder'));
      // Revert on error
      fetchPosters(selectedPlaylist.id);
    }
  };

  const resetPosterForm = () => {
    setPosterTitle('');
    setPosterDuration(10);
    setVideoUrl('');
    setAudioEnabled(false);
    setSelectedFile(null);
    setFilePreview(null);
    setVideoTotalDuration(0);
    setVideoStartTime(0);
    setVideoEndTime(0);
    setIframeUrl('');
    setIframeReloadInterval(5);
    setGalleryFiles([]);
    setGalleryImageDuration(5);
  };

  const fetchVideoDuration = async () => {
    if (!videoUrl) {
      toast.error('Vennligst skriv inn video URL først');
      return;
    }

    setFetchingVideoDuration(true);
    try {
      // Check if it's a Vimeo URL or YouTube URL
      const vimeoPatterns = [
        /(?:vimeo\.com\/)(\d+)/,  // Standard Vimeo URL
        /(?:player\.vimeo\.com\/video\/)(\d+)/,  // Vimeo player URL
      ];
      
      let vimeoId = null;
      for (const pattern of vimeoPatterns) {
        const match = videoUrl.match(pattern);
        if (match && match[1]) {
          vimeoId = match[1];
          break;
        }
      }
      
      // If not Vimeo, try YouTube
      if (!vimeoId) {
        const youtubePatterns = [
          /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,  // Standard watch URL
          /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,  // Short URL
          /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,  // Embed URL
          /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,  // Old style URL
          /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,  // YouTube Shorts
        ];
        
        let youtubeId = null;
        for (const pattern of youtubePatterns) {
          const match = videoUrl.match(pattern);
          if (match && match[1]) {
            youtubeId = match[1];
            break;
          }
        }
        
        if (!youtubeId) {
          toast.error('Kunne ikke finne YouTube eller Vimeo video ID. Sjekk URL-en.');
          setFetchingVideoDuration(false);
          return;
        }
      }
      
      // Fetch from backend
      const token = getToken();
      const durationResponse = await axios.post(`${API}/posters/get-video-duration`, {
        video_url: videoUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const duration = durationResponse.data.duration_seconds;
      const videoTitle = durationResponse.data.title;
      const thumbnailUrl = durationResponse.data.thumbnail_url;
      
      setVideoTotalDuration(duration);
      setVideoEndTime(duration);
      setPosterDuration(Math.min(duration, 60)); // Default to max 60 seconds or video length
      
      // Show success message with video title if available
      const durationText = `${Math.floor(duration / 60)}m ${duration % 60}s`;
      if (videoTitle) {
        toast.success(`Video hentet: "${videoTitle}" (${durationText})`);
      } else {
        toast.success(`Videolengde hentet: ${durationText}`);
      }
      
    } catch (error) {
      console.error('Error fetching video duration:', error);
      // Show a prompt to enter manually
      const manualDuration = prompt('Kunne ikke hente videolengde automatisk.\n\nVennligst skriv inn videolengden i sekunder:');
      if (manualDuration && !isNaN(manualDuration)) {
        const duration = parseInt(manualDuration);
        setVideoTotalDuration(duration);
        setVideoEndTime(duration);
        setPosterDuration(Math.min(duration, 60));
        toast.success(`Videolengde satt til ${Math.floor(duration / 60)}m ${duration % 60}s`);
      } else {
        toast.error('Vennligst angi videolengde manuelt nedenfor');
      }
    } finally {
      setFetchingVideoDuration(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          color: 'white'
        }}>
          Laster spillelister...
        </div>
      </CustomerLayout>
    );
  }

  // PLAYLIST VIEW (when no playlist is selected)
  if (!selectedPlaylist) {
    return (
      <CustomerLayout>
        <div style={{ padding: '2rem', minHeight: '100vh' }}>
          <div style={{ 
            maxWidth: '1400px', 
            margin: '0 auto'
          }}>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '1rem',
              marginBottom: '2rem' 
            }} className="responsive-header">
              <div>
                <h1 style={{ color: 'white', fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '0.5rem' }}>
                  {t('customerPosters.playlists')}
                </h1>
                <p style={{ color: '#9ca3af', fontSize: 'clamp(0.875rem, 2vw, 1rem)', marginBottom: '0.25rem' }}>
                  {t('customerPosters.playlistDescription')}
                </p>
                <p style={{ color: '#9ca3af', fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)' }}>
                  {t('customerPosters.playlistDetails')}
                </p>
              </div>
              <button
                onClick={handleCreatePlaylist}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                  width: '100%',
                  maxWidth: '300px'
                }}
              >
                <Plus size={20} />
                {t('customerPosters.newPlaylist')}
              </button>
            </div>

            {/* Playlists Grid */}
            {playlists.length === 0 ? (
              <div style={{
                background: 'linear-gradient(135deg, #1a1f2e 0%, #2a3142 100%)',
                borderRadius: '12px',
                padding: '3rem',
                textAlign: 'center',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <List size={48} color="#60a5fa" style={{ margin: '0 auto 1rem' }} />
                <h3 style={{ color: 'white', marginBottom: '0.5rem' }}>
                  Ingen spillelister ennå
                </h3>
                <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
                  Opprett din første spilleliste for å organisere plakater
                </p>
                <button
                  onClick={handleCreatePlaylist}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '1rem'
                  }}
                >
                  Opprett spilleliste
                </button>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
                gap: 'clamp(1rem, 2vw, 1.5rem)'
              }}>
                {playlists.map(playlist => (
                  <div
                    key={playlist.id}
                    style={{
                      background: 'linear-gradient(135deg, #1a1f2e 0%, #2a3142 100%)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                    onClick={() => setSelectedPlaylist(playlist)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.border = '1px solid rgba(59, 130, 246, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '1rem'
                    }}>
                      <h3 style={{ color: 'white', fontSize: '1.25rem', flex: 1 }}>
                        {playlist.title}
                      </h3>
                      <div style={{ display: 'flex', gap: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEditPlaylist(playlist)}
                          style={{
                            padding: '0.5rem',
                            background: 'rgba(234, 179, 8, 0.2)',
                            color: '#fbbf24',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                          title="Rediger"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeletePlaylist(playlist.id)}
                          style={{
                            padding: '0.5rem',
                            background: 'rgba(239, 68, 68, 0.2)',
                            color: '#f87171',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                          title="Slett"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ImageIcon size={16} color="#60a5fa" />
                        <span style={{ color: '#9ca3af' }}>
                          {playlist.poster_count} plakat{playlist.poster_count !== 1 ? 'er' : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{
                            background: 'rgba(59, 130, 246, 0.2)',
                            color: '#60a5fa',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '999px',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                          }}>
                            Synlig: {playlist.total_duration}s
                          </span>
                          <span style={{
                            background: 'rgba(139, 92, 246, 0.2)',
                            color: '#a78bfa',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '999px',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                          }}>
                            Skjult: {Math.floor(playlist.hide_duration / 60)}m {playlist.hide_duration % 60}s
                          </span>
                        </div>
                        {/* Toggle Switch */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                          <label style={{
                            position: 'relative',
                            display: 'inline-block',
                            width: '44px',
                            height: '24px',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={playlist.is_active !== false}
                              onChange={(e) => handleTogglePlaylistActive(playlist, e)}
                              style={{ display: 'none' }}
                            />
                            <span style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: playlist.is_active !== false ? '#50E3C2' : '#64748b',
                              borderRadius: '24px',
                              transition: '0.3s',
                              cursor: 'pointer'
                            }}>
                              <span style={{
                                position: 'absolute',
                                content: '""',
                                height: '18px',
                                width: '18px',
                                left: playlist.is_active !== false ? '23px' : '3px',
                                bottom: '3px',
                                background: 'white',
                                borderRadius: '50%',
                                transition: '0.3s'
                              }}></span>
                            </span>
                          </label>
                          <span style={{ 
                            color: playlist.is_active !== false ? '#50E3C2' : '#64748b',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                          }}>
                            {playlist.is_active !== false ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </div>
                      </div>
                      {playlist.screen_ids && playlist.screen_ids.length > 0 && (
                        <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                          Vises på {playlist.screen_ids.length} skjerm{playlist.screen_ids.length !== 1 ? 'er' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Playlist Modal */}
          {showPlaylistModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #1a1f2e 0%, #2a3142 100%)',
                borderRadius: '12px',
                padding: '2rem',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto'
              }}>
                <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>
                  {editingPlaylist ? 'Rediger spilleliste' : 'Opprett ny spilleliste'}
                </h2>

                <form onSubmit={handleSavePlaylist}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                      Tittel *
                    </label>
                    <input
                      type="text"
                      value={playlistTitle}
                      onChange={(e) => setPlaylistTitle(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '6px',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                      placeholder="F.eks. Hovedspilleliste"
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                      Varighet skjult (pause før neste visning): {Math.floor(playlistHideDuration / 60)} min {playlistHideDuration % 60}s
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="43200"
                      step="60"
                      value={playlistHideDuration}
                      onChange={(e) => setPlaylistHideDuration(parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '0.75rem', 
                      color: '#9ca3af',
                      marginTop: '0.25rem'
                    }}>
                      <span>0s (ingen pause)</span>
                      <span>43200s (12 timer)</span>
                    </div>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                      Vis på skjermer (tom = alle skjermer)
                    </label>
                    <div style={{ 
                      maxHeight: '150px', 
                      overflow: 'auto',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '6px',
                      padding: '0.75rem'
                    }}>
                      {screens.map(screen => {
                        const isLandscape = screen.orientation === 'landscape';
                        return (
                          <label key={screen.id} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            color: 'white',
                            marginBottom: '0.5rem',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={playlistScreens.includes(screen.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPlaylistScreens([...playlistScreens, screen.id]);
                                } else {
                                  setPlaylistScreens(playlistScreens.filter(id => id !== screen.id));
                                }
                              }}
                            />
                            {/* Screen orientation icon */}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                              {isLandscape ? (
                                // Landscape icon (horizontal rectangle)
                                <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                              ) : (
                                // Portrait icon (vertical rectangle)
                                <rect x="6" y="2" width="12" height="20" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                              )}
                            </svg>
                            <span style={{ color: 'white' }}>{screen.screen_name || screen.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Tidsplanlegging */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      cursor: 'pointer',
                      marginBottom: '0.75rem'
                    }}>
                      <input
                        type="checkbox"
                        checked={scheduleEnabled}
                        onChange={(e) => setScheduleEnabled(e.target.checked)}
                      />
                      Aktiver tidsplanlegging
                    </label>
                    
                    {scheduleEnabled && (
                      <>
                        {/* Dager */}
                        <div style={{ marginBottom: '1rem' }}>
                          <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                            Dager (tom = alle dager)
                          </label>
                          <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '0.5rem'
                          }}>
                            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day, idx) => {
                              const dayNames = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'];
                              return (
                                <label key={day} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.5rem',
                                  color: 'white',
                                  cursor: 'pointer'
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={scheduleDays.includes(day)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setScheduleDays([...scheduleDays, day]);
                                      } else {
                                        setScheduleDays(scheduleDays.filter(d => d !== day));
                                      }
                                    }}
                                  />
                                  <span style={{ fontSize: '0.875rem' }}>{dayNames[idx]}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        {/* Klokkeslett */}
                        <div style={{ display: 'flex', gap: '1rem' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                              Fra klokkeslett
                            </label>
                            <input
                              type="time"
                              value={scheduleTimeFrom}
                              onChange={(e) => setScheduleTimeFrom(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'rgba(0, 0, 0, 0.3)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '6px',
                                color: 'white',
                                fontSize: '1rem'
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                              Til klokkeslett
                            </label>
                            <input
                              type="time"
                              value={scheduleTimeTo}
                              onChange={(e) => setScheduleTimeTo(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: 'rgba(0, 0, 0, 0.3)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '6px',
                                color: 'white',
                                fontSize: '1rem'
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => setShowPlaylistModal(false)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Avbryt
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      {editingPlaylist ? 'Oppdater' : 'Opprett'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </CustomerLayout>
    );
  }

  // POSTERS VIEW (when playlist is selected)
  return (
    <CustomerLayout>
      <div style={{ padding: '2rem', minHeight: '100vh' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Back button and header */}
          <button
            onClick={() => {
              setSelectedPlaylist(null);
              setPosters([]);
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem'
            }}
          >
            <ArrowLeft size={20} />
            Tilbake til spillelister
          </button>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <h1 style={{ color: 'white', fontSize: '2rem', marginBottom: '0.5rem' }}>
                {selectedPlaylist.title}
              </h1>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '999px',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {posters.filter(p => !p.is_hidden).length} aktive plakater
                </span>
                <span style={{
                  background: 'rgba(139, 92, 246, 0.2)',
                  color: '#a78bfa',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '999px',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  Total varighet: {posters.filter(p => !p.is_hidden).reduce((sum, p) => sum + p.duration_seconds, 0)}s
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleUploadImage}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Upload size={20} />
                Last opp bilde
              </button>
              <button
                onClick={handleUploadVideo}
                style={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Video size={20} />
                Last opp video
              </button>
              <button
                onClick={handleUploadIframe}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Maximize size={20} />
                iFrame
              </button>
              <button
                onClick={handleUploadGallery}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Images size={20} />
                Galleri
              </button>
            </div>
          </div>

          {/* Posters Grid with Drag and Drop */}
          {posters.length === 0 ? (
            <div style={{
              background: 'linear-gradient(135deg, #1a1f2e 0%, #2a3142 100%)',
              borderRadius: '12px',
              padding: '3rem',
              textAlign: 'center',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <ImageIcon size={48} color="#60a5fa" style={{ margin: '0 auto 1rem' }} />
              <h3 style={{ color: 'white', marginBottom: '0.5rem' }}>
                Ingen plakater i denne spillelisten
              </h3>
              <p style={{ color: '#9ca3af', marginBottom: '1.5rem' }}>
                Last opp bilder eller videoer for å bygge din spilleliste
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  onClick={handleUploadImage}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Last opp bilde
                </button>
                <button
                  onClick={handleUploadVideo}
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Last opp video
                </button>
              </div>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={posters.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
                  gap: 'clamp(1rem, 2vw, 1.5rem)'
                }}>
                  {posters.map(poster => (
                    <SortablePosterCard
                      key={poster.id}
                      poster={poster}
                      imageBlobUrls={imageBlobUrls}
                      onPreview={setPreviewPoster}
                      onEdit={handleEditPoster}
                      onDelete={handleDeletePoster}
                      onToggleHidden={handleToggleHidden}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Poster Modal */}
          {showPosterModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #1a1f2e 0%, #2a3142 100%)',
                borderRadius: '12px',
                padding: '2rem',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto'
              }}>
                <h2 style={{ color: 'white', marginBottom: '1.5rem' }}>
                  {editingPoster ? 'Rediger plakat' : posterModalType === 'image' ? 'Last opp bilde' : posterModalType === 'video' ? 'Legg til video' : posterModalType === 'iframe' ? 'Legg til iFrame' : 'Legg til Galleri'}
                </h2>

                <form onSubmit={handleSavePoster}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                      Tittel *
                    </label>
                    <input
                      type="text"
                      value={posterTitle}
                      onChange={(e) => setPosterTitle(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '6px',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {!editingPoster && posterModalType === 'image' && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                        Velg bilde *
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '6px',
                          color: 'white'
                        }}
                      />
                      {filePreview && (
                        <img
                          src={filePreview}
                          alt="Preview"
                          style={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'contain',
                            marginTop: '1rem',
                            borderRadius: '6px'
                          }}
                        />
                      )}
                    </div>
                  )}

                  {posterModalType === 'video' && (
                    <>
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                          Video URL (YouTube eller Vimeo) *
                        </label>
                        <small style={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', marginBottom: '0.5rem' }}>
                          Støtter YouTube og Vimeo-videoer
                        </small>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input
                            type="url"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            required
                            style={{
                              flex: 1,
                              padding: '0.75rem',
                              background: 'rgba(0, 0, 0, 0.3)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '6px',
                              color: 'white',
                              fontSize: '1rem'
                            }}
                            placeholder="https://youtube.com/watch?v=..."
                          />
                          <button
                            type="button"
                            onClick={fetchVideoDuration}
                            disabled={fetchingVideoDuration || !videoUrl}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: fetchingVideoDuration ? '#666' : '#50E3C2',
                              color: fetchingVideoDuration ? '#ccc' : '#0F0F0F',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: fetchingVideoDuration || !videoUrl ? 'not-allowed' : 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {fetchingVideoDuration ? 'Henter...' : 'Hent lengde'}
                          </button>
                        </div>
                        {videoTotalDuration > 0 && (
                          <div style={{ 
                            marginTop: '0.5rem', 
                            fontSize: '0.875rem', 
                            color: '#50E3C2' 
                          }}>
                            ✓ Videolengde: {Math.floor(videoTotalDuration / 60)}m {videoTotalDuration % 60}s
                          </div>
                        )}
                      </div>

                      {/* Manual duration input if auto-detect fails */}
                      {videoTotalDuration === 0 && (
                        <div style={{ marginBottom: '1.5rem' }}>
                          <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                            Videolengde (sekunder) - Angi manuelt hvis auto-detect feiler
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={videoTotalDuration || ''}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              if (val > 0) {
                                setVideoTotalDuration(val);
                                setVideoEndTime(val);
                                setPosterDuration(Math.min(val, 60));
                              }
                            }}
                            placeholder="F.eks. 212 for 3min 32sek"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: 'rgba(0, 0, 0, 0.3)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '6px',
                              color: 'white',
                              fontSize: '1rem'
                            }}
                          />
                        </div>
                      )}

                      {videoTotalDuration > 0 && (
                        <div style={{ marginBottom: '1.5rem' }}>
                          <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                            Velg utsnitt av video
                          </label>
                          
                          {/* Time inputs */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                            <div>
                              <label style={{ color: '#9ca3af', fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>
                                Start-tid (sekunder)
                              </label>
                              <input
                                type="number"
                                min="0"
                                max={videoTotalDuration}
                                value={videoStartTime}
                                onChange={(e) => {
                                  const val = Math.max(0, Math.min(parseInt(e.target.value) || 0, videoEndTime - 1));
                                  setVideoStartTime(val);
                                  setPosterDuration(videoEndTime - val);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  background: 'rgba(0, 0, 0, 0.3)',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  borderRadius: '6px',
                                  color: 'white'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ color: '#9ca3af', fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>
                                Slutt-tid (sekunder)
                              </label>
                              <input
                                type="number"
                                min={videoStartTime + 1}
                                max={videoTotalDuration}
                                value={videoEndTime}
                                onChange={(e) => {
                                  const val = Math.max(videoStartTime + 1, Math.min(parseInt(e.target.value) || 0, videoTotalDuration));
                                  setVideoEndTime(val);
                                  setPosterDuration(val - videoStartTime);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  background: 'rgba(0, 0, 0, 0.3)',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  borderRadius: '6px',
                                  color: 'white'
                                }}
                              />
                            </div>
                          </div>

                          {/* Dual range slider */}
                          <div style={{ position: 'relative', padding: '1rem 0' }}>
                            {/* Background track */}
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '2px',
                              transform: 'translateY(-50%)'
                            }}></div>
                            
                            {/* Active range highlight */}
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: `${(videoStartTime / videoTotalDuration) * 100}%`,
                              right: `${100 - (videoEndTime / videoTotalDuration) * 100}%`,
                              height: '4px',
                              background: '#50E3C2',
                              borderRadius: '2px',
                              transform: 'translateY(-50%)'
                            }}></div>
                            
                            {/* Start slider - only pointer events on left half */}
                            <input
                              type="range"
                              min="0"
                              max={videoTotalDuration}
                              value={videoStartTime}
                              onChange={(e) => {
                                const val = Math.min(parseInt(e.target.value), videoEndTime - 1);
                                setVideoStartTime(val);
                                setPosterDuration(videoEndTime - val);
                              }}
                              className="video-range-slider video-range-start"
                              style={{
                                position: 'absolute',
                                width: '100%',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                appearance: 'none',
                                background: 'transparent',
                                outline: 'none',
                                zIndex: videoStartTime > videoTotalDuration / 2 ? 5 : 3,
                                pointerEvents: 'auto'
                              }}
                            />
                            
                            {/* End slider - only pointer events on right half */}
                            <input
                              type="range"
                              min="0"
                              max={videoTotalDuration}
                              value={videoEndTime}
                              onChange={(e) => {
                                const val = Math.max(parseInt(e.target.value), videoStartTime + 1);
                                setVideoEndTime(val);
                                setPosterDuration(val - videoStartTime);
                              }}
                              className="video-range-slider video-range-end"
                              style={{
                                position: 'absolute',
                                width: '100%',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                appearance: 'none',
                                background: 'transparent',
                                outline: 'none',
                                zIndex: videoEndTime < videoTotalDuration / 2 ? 5 : 4,
                                pointerEvents: 'auto'
                              }}
                            />
                          </div>

                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            fontSize: '0.75rem', 
                            color: '#9ca3af',
                            marginTop: '0.5rem'
                          }}>
                            <span>0s</span>
                            <span style={{ color: '#50E3C2', fontWeight: '600' }}>
                              Varighet: {videoEndTime - videoStartTime}s
                            </span>
                            <span>{videoTotalDuration}s</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {posterModalType === 'image' && !editingPoster && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                        Varighet synlig: {posterDuration}s
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="300"
                        step="5"
                        value={posterDuration}
                        onChange={(e) => setPosterDuration(parseInt(e.target.value))}
                        style={{ width: '100%' }}
                      />
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        fontSize: '0.75rem', 
                        color: '#9ca3af',
                        marginTop: '0.25rem'
                      }}>
                        <span>5 sek</span>
                        <span>300 sek (5 min)</span>
                      </div>
                      
                      {/* Fine-tune seconds input */}
                      <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ color: '#9ca3af', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                          Finjuster:
                        </label>
                        <input
                          type="number"
                          min="5"
                          max="300"
                          step="1"
                          value={posterDuration}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 5 && val <= 300) {
                              setPosterDuration(val);
                            }
                          }}
                          style={{
                            width: '100px',
                            padding: '0.5rem',
                            background: 'rgba(0, 0, 0, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            color: 'white',
                            fontSize: '0.875rem'
                          }}
                        />
                        <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>sekunder</span>
                      </div>
                    </div>
                  )}

                  {posterModalType === 'video' && !editingPoster && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ 
                        color: 'white', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={audioEnabled}
                          onChange={(e) => setAudioEnabled(e.target.checked)}
                        />
                        Aktiver lyd
                      </label>
                    </div>
                  )}

                  {posterModalType === 'iframe' && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                        iFrame URL *
                      </label>
                      <input
                        type="url"
                        value={iframeUrl}
                        onChange={(e) => setIframeUrl(e.target.value)}
                        required={posterModalType === 'iframe'}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '6px',
                          color: 'white',
                          fontSize: '1rem'
                        }}
                        placeholder="https://example.com/side"
                      />
                      <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                        Skriv inn full URL til siden som skal vises i iFrame. Alle URL-er er tillatt.
                      </p>
                      <p style={{ color: '#60a5fa', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                        💡 Eksempel: <a 
                          href="https://mon.ruter.no/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#60a5fa', textDecoration: 'underline' }}
                        >
                          Ruter sin buss og tog-info
                        </a> (åpnes i nytt vindu)
                      </p>
                    </div>
                  )}

                  {posterModalType === 'iframe' && (
                    <>
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                          Visningstid: {posterDuration} sekunder
                        </label>
                        <input
                          type="range"
                          min="5"
                          max="300"
                          step="5"
                          value={posterDuration}
                          onChange={(e) => setPosterDuration(parseInt(e.target.value))}
                          style={{ width: '100%' }}
                        />
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          fontSize: '0.75rem', 
                          color: '#9ca3af',
                          marginTop: '0.25rem'
                        }}>
                          <span>5 sek</span>
                          <span>300 sek (5 min)</span>
                        </div>
                        
                        {/* Fine-tune seconds input */}
                        <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <label style={{ color: '#9ca3af', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                            Finjuster:
                          </label>
                          <input
                            type="number"
                            min="5"
                            max="300"
                            step="1"
                            value={posterDuration}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              if (val >= 5 && val <= 300) {
                                setPosterDuration(val);
                              }
                            }}
                            style={{
                              width: '100px',
                              padding: '0.5rem',
                              background: 'rgba(0, 0, 0, 0.3)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '6px',
                              color: 'white',
                              fontSize: '0.875rem'
                            }}
                          />
                          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>sekunder</span>
                        </div>
                        
                        <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          Hvor lenge skal denne plakaten vises før neste plakat?
                        </p>
                      </div>
                      
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                          Reload-intervall: {iframeReloadInterval} min
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="60"
                          step="1"
                          value={iframeReloadInterval}
                          onChange={(e) => setIframeReloadInterval(parseInt(e.target.value))}
                          style={{ width: '100%' }}
                        />
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          fontSize: '0.75rem', 
                          color: '#9ca3af',
                          marginTop: '0.25rem'
                        }}>
                          <span>1 min</span>
                          <span>60 min (1 time)</span>
                        </div>
                        <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          Hvor ofte skal iFrame-innholdet oppdateres? (Standard: 5 minutter)
                        </p>
                      </div>
                    </>
                  )}

                  {posterModalType === 'gallery' && (
                    <>
                      {editingPoster && editingPoster.gallery_images && (
                        <div style={{ marginBottom: '1.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <label style={{ color: 'white' }}>
                              Nåværende bilder ({editingPoster.gallery_images.length}/50)
                            </label>
                          </div>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                            gap: '0.5rem',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            padding: '0.5rem',
                            background: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: '6px'
                          }}>
                            {editingPoster.gallery_images.map((url, index) => (
                              <div key={index} style={{ position: 'relative' }}>
                                <img
                                  src={url}
                                  alt={`Bilde ${index + 1}`}
                                  style={{
                                    width: '100%',
                                    height: '100px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: '2px solid rgba(255, 255, 255, 0.1)',
                                    opacity: removingGalleryImage === index ? 0.5 : 1
                                  }}
                                />
                                <div style={{
                                  position: 'absolute',
                                  bottom: '4px',
                                  left: '4px',
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  fontSize: '0.75rem'
                                }}>
                                  {index + 1}
                                </div>
                                {/* Delete button for each image */}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveGalleryImage(index)}
                                  disabled={removingGalleryImage !== null || editingPoster.gallery_images.length <= 1}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    background: 'rgba(220, 53, 69, 0.9)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    cursor: editingPoster.gallery_images.length <= 1 ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: editingPoster.gallery_images.length <= 1 ? 0.5 : 1,
                                    transition: 'transform 0.2s'
                                  }}
                                  title={editingPoster.gallery_images.length <= 1 ? 'Kan ikke fjerne siste bilde' : 'Fjern bilde'}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                          
                          {/* Add more images section */}
                          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(80, 227, 194, 0.1)', borderRadius: '6px', border: '1px dashed rgba(80, 227, 194, 0.3)' }}>
                            <label style={{ color: '#50E3C2', display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                              <Plus size={16} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                              Legg til flere bilder
                            </label>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              disabled={addingGalleryImages}
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                if (files.length > 0) {
                                  handleAddGalleryImages(files);
                                }
                                e.target.value = '';
                              }}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                background: 'rgba(0, 0, 0, 0.3)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '6px',
                                color: 'white',
                                fontSize: '0.9rem'
                              }}
                            />
                            {addingGalleryImages && (
                              <p style={{ color: '#50E3C2', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                ⏳ Laster opp bilder...
                              </p>
                            )}
                            <p style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                              Velg flere bilder for å legge dem til galleriet. Maks 50 bilder totalt.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {!editingPoster && (
                        <div style={{ marginBottom: '1.5rem' }}>
                          <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                            Last opp bilder *
                          </label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            if (files.length > 50) {
                              toast.error('Maksimum 50 bilder tillatt');
                              return;
                            }
                            setGalleryFiles(files);
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(0, 0, 0, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '6px',
                            color: 'white',
                            fontSize: '1rem'
                          }}
                        />
                        <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          Velg flere bilder samtidig. Maks 50 bilder. Alle bilder skaleres til 1920px bredde.
                        </p>
                        {galleryFiles.length > 0 && (
                          <>
                            <p style={{ color: '#10b981', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                              ✓ {galleryFiles.length} bilde{galleryFiles.length !== 1 ? 'r' : ''} valgt
                            </p>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                              gap: '0.5rem',
                              marginTop: '1rem',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              padding: '0.5rem',
                              background: 'rgba(0, 0, 0, 0.2)',
                              borderRadius: '6px'
                            }}>
                              {galleryFiles.map((file, index) => (
                                <div key={index} style={{ position: 'relative' }}>
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Preview ${index + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '100px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '2px solid rgba(255, 255, 255, 0.1)'
                                    }}
                                  />
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '4px',
                                    right: '4px',
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    color: 'white',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '0.75rem'
                                  }}>
                                    {index + 1}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        </div>
                      )}

                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                          Varighet per bilde: {galleryImageDuration} sekunder
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="30"
                          step="1"
                          value={galleryImageDuration}
                          onChange={(e) => setGalleryImageDuration(parseInt(e.target.value))}
                          style={{ width: '100%' }}
                        />
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          fontSize: '0.75rem', 
                          color: '#9ca3af',
                          marginTop: '0.25rem'
                        }}>
                          <span>1 sek</span>
                          <span>30 sek</span>
                        </div>
                        
                        {/* Fine-tune seconds input */}
                        <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <label style={{ color: '#9ca3af', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                            Finjuster:
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="30"
                            step="1"
                            value={galleryImageDuration}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              if (val >= 1 && val <= 30) {
                                setGalleryImageDuration(val);
                              }
                            }}
                            style={{
                              width: '100px',
                              padding: '0.5rem',
                              background: 'rgba(0, 0, 0, 0.3)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '6px',
                              color: 'white',
                              fontSize: '0.875rem'
                            }}
                          />
                          <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>sekunder</span>
                        </div>
                      </div>

                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                          Varighet synlig: {(editingPoster?.gallery_images?.length || galleryFiles.length) * galleryImageDuration} sekunder
                        </label>
                        <div style={{
                          width: '100%',
                          height: '40px',
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#10b981',
                          fontSize: '1.25rem',
                          fontWeight: 'bold'
                        }}>
                          {editingPoster?.gallery_images?.length || galleryFiles.length} bilder × {galleryImageDuration}s = {(editingPoster?.gallery_images?.length || galleryFiles.length) * galleryImageDuration}s
                        </div>
                        <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          Total varighet beregnes automatisk
                        </p>
                      </div>
                    </>
                  )}

                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPosterModal(false);
                        resetPosterForm();
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Avbryt
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      {editingPoster ? 'Oppdater' : 'Lagre'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Preview Modal */}
          {previewPoster && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '2rem'
              }}
              onClick={() => setPreviewPoster(null)}
            >
              <div style={{ maxWidth: '90%', maxHeight: '90%', position: 'relative' }}>
                {previewPoster.poster_type === 'image' && previewPoster.image_url && (
                  <img
                    src={imageBlobUrls[previewPoster.id] || (previewPoster.image_url.startsWith('http') ? previewPoster.image_url : `${BACKEND_URL}${previewPoster.image_url}`)}
                    alt={previewPoster.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '90vh',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                  />
                )}
                <button
                  onClick={() => setPreviewPoster(null)}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.75rem 1.5rem',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Lukk
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerPosters;
